import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { useGetApplications } from "@/hooks/useApplications";
import { APPLICATION_CATEGORY, PATH } from "@/lib/enums";
import { cn, humanizeString } from "@/lib/utils";
import { ApplicationCategory, ApplicationSummary } from "@/types";
import { formatDistanceToNow } from "date-fns";
import {
  AppWindow,
  ArrowRight,
  Database,
  Globe,
  Layers,
  Search,
  Settings2,
} from "lucide-react";
import { KeyboardEvent, useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import LoadingSpinner from "../Common/Spinner";
import { ProjectSettingsDialog } from "../Projects/ProjectSettingsDialog";

const categoryFilters = [
  {
    label: "Applications",
    description: "Runtime workloads",
    value: APPLICATION_CATEGORY.APPLICATIONS,
    icon: AppWindow,
  },
  {
    label: "Static sites",
    description: "Edge cached content",
    value: APPLICATION_CATEGORY.STATIC_SITES,
    icon: Globe,
  },
  {
    label: "Databases",
    description: "Managed data stores",
    value: APPLICATION_CATEGORY.DATABASES,
    icon: Database,
  },
];

const statusTone: Record<string, string> = {
  complete: "bg-emerald-500/10 text-emerald-200 border border-emerald-400/30",
  new: "bg-blue-500/10 text-blue-200 border border-blue-400/30",
  in_progress: "bg-amber-500/10 text-amber-200 border border-amber-400/30",
  failed: "bg-rose-500/10 text-rose-200 border border-rose-400/30",
};

export function ApplicationsPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const params = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );
  const activeCategory =
    (params.get("category") as ApplicationCategory | null) ||
    APPLICATION_CATEGORY.APPLICATIONS;

  const [inputValue, setInputValue] = useState("");
  const [searchTerm, setSearchTerm] = useState("");
  const [offset, setOffset] = useState(0);
  const limit = 6;
  const [selectedProjectId, setSelectedProjectId] = useState<string | null>(
    null,
  );

  const applicationsQuery = useGetApplications(
    limit,
    offset,
    searchTerm,
    activeCategory,
  );

  useEffect(() => {
    const handler = setTimeout(() => {
      setSearchTerm(inputValue);
      setOffset(0);
    }, 400);
    return () => clearTimeout(handler);
  }, [inputValue]);

  const projectGroups = applicationsQuery.data?.items ?? [];
  const hasNext = applicationsQuery.data?.hasNext ?? false;
  const selectedProject = useMemo(() => {
    if (!selectedProjectId) {
      return null;
    }
    return (
      projectGroups.find(({ project }) => project.id === selectedProjectId)
        ?.project ?? null
    );
  }, [projectGroups, selectedProjectId]);

  const handleCategoryChange = (value: ApplicationCategory) => {
    const nextSearch =
      value === APPLICATION_CATEGORY.APPLICATIONS ? "" : `?category=${value}`;
    navigate(`${PATH.APPLICATIONS}${nextSearch}`);
    setOffset(0);
  };

  const handleNext = () => {
    if (hasNext) setOffset((prev) => prev + limit);
  };

  const handlePrev = () => {
    if (offset > 0) setOffset((prev) => Math.max(prev - limit, 0));
  };

  const openDeployDetail = (deployId: string) => {
    navigate(`${PATH.APPLICATIONS}/${deployId}`, {
      state: { category: activeCategory },
    });
  };

  const makeCardKeyHandler =
    (deployId: string) => (event: KeyboardEvent<HTMLDivElement>) => {
      if (event.key === "Enter" || event.key === " ") {
        event.preventDefault();
        openDeployDetail(deployId);
      }
    };

  return (
    <div className="min-h-full w-full pb-4">
      <div className="border-b border-slate-800 bg-slate-900/90"></div>
      <div className="p-4 md:p-6 lg:p-8 space-y-4 md:space-y-6 lg:space-y-8 w-full">
        <div className="flex flex-col gap-3 md:gap-4 sm:flex-row sm:items-center sm:justify-between pt-12 lg:pt-0">
          <div>
            <h1 className="text-2xl md:text-3xl lg:text-4xl font-bold mb-1">
              {categoryFilters.find((c) => c.value === activeCategory)?.label ??
                "Applications"}
            </h1>
          </div>
          <Button
            onClick={() =>
              navigate(PATH.CREATE_DEPLOY, {
                state: { from: location.pathname, search: location.search },
              })
            }
            className="cyber-button w-full sm:w-auto"
          >
            Create{" "}
            {categoryFilters
              .find((c) => c.value === activeCategory)
              ?.label?.toLowerCase()
              .replace(/s$/, "") ?? "application"}
          </Button>
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 md:gap-3">
          {categoryFilters.map((filter) => {
            const Icon = filter.icon;
            const isActive = activeCategory === filter.value;
            return (
              <button
                key={filter.value}
                onClick={() => handleCategoryChange(filter.value)}
                className={cn(
                  "flex items-center gap-2 md:gap-3 px-3 md:px-4 py-2.5 md:py-2 rounded-lg border transition-all text-sm min-h-[60px] md:min-h-0",
                  isActive
                    ? "bg-cyan-500/10 border-cyan-400/60 text-white shadow-lg shadow-cyan-500/20"
                    : "bg-slate-900/40 border-slate-700 text-slate-300 hover:border-cyan-400/40",
                )}
              >
                <Icon className="h-5 w-5 md:h-4 md:w-4 flex-shrink-0" />
                <div className="text-left flex-1">
                  <p className="font-semibold text-sm md:text-base">
                    {filter.label}
                  </p>
                  <p className="text-xs text-slate-400 hidden sm:block">
                    {filter.description}
                  </p>
                </div>
              </button>
            );
          })}
        </div>

        <Card className="holo-card border-cyan-500/40">
          <CardContent className="space-y-4 md:space-y-6 p-4 md:p-6 lg:p-8">
            <div className="flex flex-col gap-3 md:gap-4 lg:flex-row lg:items-center lg:justify-between">
              <div className="relative w-full lg:max-w-sm">
                <Search className="absolute left-3 md:left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-cyan-300/60" />
                <Input
                  value={inputValue}
                  onChange={(event) => setInputValue(event.target.value)}
                  placeholder="Search by repo, domain, or branch"
                  className="pl-10 md:pl-11 bg-slate-900/60 border-slate-700 text-white placeholder:text-slate-500 h-11"
                />
              </div>
              <div className="flex gap-2 text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-cyan-300/70">
                <span>Showing</span>
                <span>{applicationsQuery.data?.total ?? 0} entries</span>
              </div>
            </div>

            {applicationsQuery.isLoading ? (
              <LoadingSpinner />
            ) : projectGroups.length === 0 ? (
              <div className="rounded-2xl border border-dashed border-cyan-500/40 p-12 text-center text-slate-300">
                <Layers className="mx-auto h-10 w-10 mb-4 text-cyan-400" />
                <p className="font-semibold mb-1">No projects yet</p>
                <p className="text-sm">
                  Deploy your first service to start populating this view.
                </p>
              </div>
            ) : (
              <div className="space-y-4 md:space-y-5">
                {projectGroups.map(({ project, deployments }) => (
                  <div
                    key={project.id}
                    className="rounded-xl border border-cyan-500/30 bg-slate-950/80 p-4 md:p-5 shadow-lg shadow-cyan-500/10 space-y-4 md:space-y-5"
                  >
                    <div className="flex items-start justify-between gap-3">
                      <div className="min-w-0 flex-1">
                        <p className="text-xs uppercase tracking-[0.2em] md:tracking-[0.3em] text-cyan-300/70">
                          Project
                        </p>
                        <h3 className="text-lg md:text-xl font-semibold truncate">
                          {project.name}
                        </h3>
                        <p className="text-xs md:text-sm text-slate-400 break-all">
                          {project.repoUri ?? "Repository pending connection"}
                        </p>
                        {project.config?.rootDirectory ? (
                          <p className="text-xs text-slate-500">
                            Root: {project.config.rootDirectory}
                          </p>
                        ) : null}
                        {project.config?.deployType ? (
                          <p className="text-xs text-slate-500">
                            Type: {humanizeString(project.config.deployType)}
                          </p>
                        ) : null}
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <Button
                          variant="outline"
                          className="border-cyan-500/40 text-cyan-200 hover:text-cyan-100 text-xs"
                          onClick={() => setSelectedProjectId(project.id)}
                        >
                          <Settings2 className="h-4 w-4 mr-2" />
                          Configure
                        </Button>
                        <Badge className="rounded-full bg-slate-900 border border-cyan-500/30 text-cyan-200 text-xs whitespace-nowrap flex-shrink-0">
                          {deployments.length}{" "}
                          {deployments.length === 1
                            ? "deployment"
                            : "deployments"}
                        </Badge>
                      </div>
                    </div>

                    {deployments.length === 0 ? (
                      <div className="rounded-lg border border-dashed border-cyan-500/30 bg-slate-900/50 p-6 text-sm text-slate-300">
                        No deployments yet. Trigger your first build to see it
                        here.
                      </div>
                    ) : (
                      <div className="space-y-2 md:space-y-3">
                        {deployments.map((deployment: ApplicationSummary) => {
                          const badgeTone =
                            statusTone[deployment.status] ??
                            "bg-slate-800 text-slate-200 border border-slate-700";
                          const domain =
                            deployment.customDomain ||
                            deployment.subDomain ||
                            "Pending";
                          return (
                            <div
                              key={deployment.id}
                              role="button"
                              tabIndex={0}
                              onKeyDown={makeCardKeyHandler(deployment.id)}
                              onClick={() => openDeployDetail(deployment.id)}
                              className="rounded-xl md:rounded-2xl border border-slate-800 bg-slate-950/60 px-3 md:px-4 py-3 md:py-4 flex flex-col gap-3 md:flex-row md:items-center md:justify-between cursor-pointer transition hover:border-cyan-400 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-cyan-400/70"
                            >
                              <div className="min-w-0 flex-1">
                                <p className="text-sm font-semibold text-white truncate">
                                  {deployment.environment} · {deployment.branch}
                                </p>
                                <p className="text-xs text-slate-400">
                                  Region {deployment.region} · Updated{" "}
                                  {formatDistanceToNow(
                                    new Date(deployment.updatedAt),
                                    {
                                      addSuffix: true,
                                    },
                                  )}
                                </p>
                              </div>
                              <div className="flex flex-col gap-2 text-sm text-slate-300 sm:flex-row sm:items-center sm:gap-4 md:gap-6">
                                <div className="min-w-0 flex-1 sm:flex-initial">
                                  <p className="text-xs uppercase tracking-[0.15em] md:tracking-[0.2em] text-cyan-300/70">
                                    Domain
                                  </p>
                                  <p className="font-medium break-all text-xs md:text-sm">
                                    {domain}
                                  </p>
                                </div>
                                <Badge
                                  className={`${badgeTone} rounded-full text-xs self-start sm:self-auto`}
                                >
                                  {humanizeString(deployment.status)}
                                </Badge>
                              </div>
                              <Button
                                variant="ghost"
                                className="text-cyan-300 hover:text-cyan-100 w-full sm:w-auto text-sm"
                                onClick={(event) => {
                                  event.stopPropagation();
                                  openDeployDetail(deployment.id);
                                }}
                              >
                                View deploy
                                <ArrowRight className="ml-2 h-4 w-4" />
                              </Button>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}

            <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between pt-2 text-sm text-slate-400">
              <div className="text-center sm:text-left">
                Page {offset / limit + 1} ·{" "}
                <span>{applicationsQuery.data?.total ?? 0} projects</span>
              </div>
              <div className="flex gap-2">
                <Button
                  variant="ghost"
                  disabled={offset === 0}
                  onClick={handlePrev}
                  className="text-slate-200 hover:text-white disabled:opacity-40 flex-1 sm:flex-initial"
                >
                  Previous
                </Button>
                <Button
                  variant="ghost"
                  disabled={!hasNext}
                  onClick={handleNext}
                  className="text-slate-200 hover:text-white disabled:opacity-40 flex-1 sm:flex-initial"
                >
                  Next
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
      <ProjectSettingsDialog
        project={selectedProject}
        open={selectedProjectId !== null}
        onOpenChange={(open) => {
          if (!open) {
            setSelectedProjectId(null);
          }
        }}
      />
    </div>
  );
}
